import React, { Component } from "react";
import { Helmet } from "react-helmet";
import { observer, inject } from "mobx-react";
import {
  FormGroup,
  ControlGroup,
  TextArea,
  Button, Intent
} from "@blueprintjs/core";
import moment from 'moment-timezone';
import DateTimeInput from "components/DateTimeInput";
import WhenPicker from "components/Job/WhenPicker";
import ClientAutocomplete from "components/ClientAutocomplete";
import LocationAutocomplete from "components/LocationAutocomplete";
import ProjectAutocomplete from "components/Job/ProjectAutocomplete";
import { newJobTimes, isEmptyJobStart } from "helpers/jobTimes";
import ServiceTypePicker from "components/ServiceTypePicker";
import TechnicianPicker from "components/TechnicianPicker";
import AddProjectModal from "components/Project/AddProjectModal";
import AddLocationModal from "components/Client/AddLocationModal";
import AddClientModal from "components/Client/AddClientModal";
import { FormSection, Choice, FieldError, ClientFlags } from "components/FormBits";
import { priorityMapping } from "helpers/priorityTag";
import { Api } from "services/Api";
import Toast from 'helpers/toaster';
import FormDrawer from "components/FormDrawer";
import { observable } from "mobx";

// Same values and labels as PriorityPicker ("normal" is shown as Low).
const PRIORITIES = ['normal', 'medium', 'high', 'urgent', 'waiting_on_parts'].map((value) => {
  const { emoji, label } = priorityMapping(value);
  return { value, label: `${emoji} ${label}` };
});

// Two ways to set when the job happens. The form always opens on Start and end.
const SCHEDULE_MODES = [
  { value: 'range', label: 'Start and end' },
  { value: 'day', label: 'Day and time' },
];

// Field wrappers, in the order they appear: the first with a problem gets focus.
const FIELD_IDS = {
  client: 'job-field-client',
  service_type: 'job-field-service-type',
  title: 'job-field-title',
  reason: 'job-field-reason',
  end: 'job-field-end',
};

const minutesBetween = (start, end) =>
  start && end ? moment(end).diff(moment(start), 'minutes') : null;

// New/Edit job, in a side panel. Mounted afresh each time it opens
// (pages/jobs/new.js); `isOpen` and `onClose` come from there.
@inject("store", "projectStore", "jobStore", "userStore")
@observer
class PersistJob extends Component {
  @observable isOpenAddProjectDialog = false;
  @observable isOpenAddLocationDialog = false;
  @observable isOpenAddClientDialog = false;
  @observable startDateDefault = moment();
  @observable endDateDefault = moment().add(1, 'hour');
  @observable scheduleMode = 'range';
  @observable techsVacationConflicts = [];
  // Set on the first Save; from then on problems show (and clear) as you type.
  @observable submitted = false;

  componentDidMount() {
    let { object } = this.props.jobStore.persistJob;
    // A new job nobody gave times to (still the store's empty-job start): start
    // 15 minutes from now, rounded to 5 minutes, for an hour. Times passed in
    // (dispatch board, bookings) are kept.
    if (object && !object.id && (!object.start_date || isEmptyJobStart(object.start_date))) {
      const { start, end } = newJobTimes();
      object.start_date = start;
      object.end_date = end;
      this.startDateDefault = start;
      this.endDateDefault = end;
    }
    // Opened for a client (client page): use its location if it has only one.
    if (object && object.client_id && !object.location_id && !object.location) {
      this.useOnlyLocation(object.client_id);
    }
  }

  componentWillReceiveProps(nextProps) {
    this.forceUpdate();
  }

  // If the client has exactly one location, pick it (never replaces a choice).
  useOnlyLocation(clientId) {
    const search = { term: '', per_page: 2, page: 1, order: { name: 'asc' }, where: { client_id: clientId } };
    Api.post('/locations/search', { search })
      .then((response) => {
        const { object } = this.props.jobStore.persistJob;
        const locations = (response.data && response.data.data) || [];
        if (locations.length === 1 && object && object.client_id === clientId && !object.location_id && !object.location) {
          object.location_id = locations[0].id;
          object.location = locations[0];
        }
      })
      .catch(() => {});
  }

  onServiceTypeSelect(selectedOptions) {
    let { object } = this.props.jobStore.persistJob;
    object.selectedServiceType = selectedOptions;
    // Unticking the service type clears it, so the required check sees it.
    object.service_type_id = selectedOptions.length > 0 ? selectedOptions[0].value : null;
  }

  onTechnicianChange(technicians) {
    let {
      props: {
        jobStore: {
          persistJob: { object }
        }
      }
    } = this;
    let selectedValues = technicians.map(status => status.value);
    object.technician_ids = selectedValues;
    this.checkTechnicianVacationConflicts(object.technician_ids, object.start_date, object.end_date)
  }

  checkTechnicianVacationConflicts(technician_ids, start_date, end_date) {
    this.props.userStore.checkTechnicianVacationConflicts(technician_ids, start_date, end_date)
      .then((response) => {
        this.techsVacationConflicts = response.data.conflicts;
      })
  }

  // Times moved: check the assigned technicians again.
  recheckConflicts() {
    const { object } = this.props.jobStore.persistJob;
    if (object.technician_ids && object.technician_ids.length > 0) {
      this.checkTechnicianVacationConflicts(object.technician_ids, object.start_date, object.end_date);
    }
  }

  // What's missing or wrong, by field. Same rules as before (title, reason for
  // call, service type, PO number when "PO provided" is ticked), plus a
  // customer and an end after the start.
  validate() {
    const { object } = this.props.jobStore.persistJob;
    const errors = {};
    if (!object.client_id && !object.client) {
      errors.client = 'Choose the customer for this job.';
    }
    if (object.service_type_id === '' || object.service_type_id === null) {
      errors.service_type = 'Choose a service type.';
    }
    if (object.title === '' || object.title === null) {
      errors.title = 'Give the job a title.';
    }
    if (object.reason_for_call === '' || object.reason_for_call === null) {
      errors.reason = 'Say why the customer called.';
    }
    if (object.start_date && object.end_date && moment(object.end_date).isBefore(moment(object.start_date))) {
      errors.end = 'End must be after the start.';
    }
    return errors;
  }

  // On Save: true when fine; otherwise shows the problems and goes to the first.
  checkForm() {
    this.submitted = true;
    const errors = this.validate();
    const first = Object.keys(FIELD_IDS).find((key) => errors[key]);
    if (!first) return true;
    // After the panel re-renders (More options may need to open first).
    setTimeout(() => {
      const wrapper = document.getElementById(FIELD_IDS[first]);
      if (!wrapper) return;
      wrapper.scrollIntoView({ block: 'center', behavior: 'smooth' });
      const target = wrapper.querySelector('input:not([type=hidden]):not([readonly]), textarea, button');
      if (target) target.focus({ preventScroll: true });
    }, 60);
    return false;
  }

  createJob() {
    if (!this.checkForm()) return;
    this.props.jobStore.createJob().then(function (response) {
      const data = response.data.data;
      Toast.show({ icon: 'thumbs-up', message: <p>{`Job #${data.attributes.event_number} has been successfully created. Please `}<a href={`/dashboard/jobs/${data.attributes.job_id}`}>click here</a> to go to the job page.</p>, intent: Intent.SUCCESS })
    })
  }

  updateJob() {
    if (!this.checkForm()) return;
    this.props.jobStore.updateSidebarJob()
      .then((response) => {
        Toast.show({ icon: 'thumbs-up', message: 'Job updated successfully!', intent: Intent.SUCCESS })
      })
      .catch((error) => {
        if (error.response && error.response.data && error.response.data.errors) {
          Toast.show({ icon: 'warning-sign', message: error.response.data.errors, intent: Intent.DANGER })
        } else {
          Toast.show({ icon: 'warning-sign', message: 'Something went wrong. Unable to update job!', intent: Intent.DANGER })
        }
      })
  }

  handleCloseProjectDailog() {
    this.isOpenAddProjectDialog = false;
  }

  handleCloseClientDailog() {
    this.isOpenAddClientDialog = false;
  }

  handleCloseLocationDailog() {
    this.isOpenAddLocationDialog = false;
  }

  onSuccessAddLocation(location) {
    let { object } = this.props.jobStore.persistJob;
    this.isOpenAddLocationDialog = false;
    object.location_id = location.id;
    object.location = location;
  }

  onSuccessAddClient(client) {
    let { object } = this.props.jobStore.persistJob;
    this.isOpenAddClientDialog = false;
    object.client_id = client.id;
    object.client = client;
  }

  onSuccessAddProject(project) {
    this.isOpenAddProjectDialog = false;
  }

  onLocationClear = () => {
    let { object } = this.props.jobStore.persistJob;
    object.location_id = null;
    object.location = null;
  };

  onClientClear = () => {
    let { object } = this.props.jobStore.persistJob;
    object.client_id = null;
    object.client = null;
    this.onLocationClear();
  };

  // Moving the start keeps the visit's length (an hour if it had none).
  onStartDateChange = time => {
    let { object } = this.props.jobStore.persistJob;
    let length = minutesBetween(object.start_date, object.end_date);
    if (!(length > 0)) length = 60;
    object.start_date = moment(time);
    object.end_date = moment(time).add(length, 'minutes');
    this.startDateDefault = moment(time);
    this.endDateDefault = moment(object.end_date);
    this.recheckConflicts();
  };

  onEndDateChange = time => {
    let { object } = this.props.jobStore.persistJob;
    object.end_date = moment(time);
    this.endDateDefault = moment(time);
    this.recheckConflicts();
  }

  setScheduleMode(mode) {
    this.scheduleMode = mode;
  }

  render() {
    let { object, isLoading } = this.props.jobStore.persistJob;
    let { isOpen, onClose } = this.props;
    // While the panel slides out the job has already been cleared; keep the
    // title it opened with.
    if (isOpen) {
      this.isEdit = !!(object && object.id);
    }
    const isEdit = this.isEdit;
    const title = isEdit ? "Edit job" : "New job";
    const errors = this.submitted ? this.validate() : {};
    const priority = (object.priority || 'normal').toLowerCase();

    return (
      <React.Fragment>
        {isOpen && (
          <Helmet>
            <title>{title}</title>
          </Helmet>
        )}
        <FormDrawer
          className="job-form"
          isOpen={isOpen}
          title={title}
          icon={isEdit ? "edit" : "calendar"}
          size="min(760px, 100vw)"
          onClose={onClose}
          busy={!!isLoading}
          footer={
            <>
              <Button text="Cancel" onClick={onClose} disabled={!!isLoading} />
              {isEdit ? (
                <Button
                  icon="floppy-disk"
                  text="Save changes"
                  intent={Intent.PRIMARY}
                  onClick={this.updateJob.bind(this)}
                  loading={isLoading}
                />
              ) : (
                <Button
                  icon="add"
                  text="Create job"
                  intent={Intent.PRIMARY}
                  onClick={this.createJob.bind(this)}
                  loading={isLoading}
                />
              )}
            </>
          }
        >
          <FormSection title="Customer" hint="Who the job is for and where you're going.">
            <FormGroup
              label={<span className="fb-label">Customer<span className="fb-required" aria-hidden="true"> *</span></span>}
              labelFor="job-client"
              className={errors.client ? 'has-error' : ''}
            >
              <div id={FIELD_IDS.client}>
                <ControlGroup fill={true} vertical={false} className="fb-picker">
                  <ClientAutocomplete
                    showDetails={true}
                    inputProps={{
                      id: "job-client",
                      placeholder: "Search by name, phone number or email",
                      'aria-required': true,
                    }}
                    value={object.client}
                    onClientSelect={client => {
                      if (object && client) {
                        object.client_id = client.id;
                        object.client = client;
                        if (!object.location_id && !object.location) {
                          this.useOnlyLocation(client.id);
                        }
                      } else {
                        this.onClientClear();
                      }
                    }}
                  />
                  <Button
                    icon="add"
                    text="New client"
                    onClick={() => {
                      this.isOpenAddClientDialog = true;
                    }}
                    className="fb-add-button"
                  />
                </ControlGroup>
              </div>
              <FieldError>{errors.client}</FieldError>
              <ClientFlags client={object.client} />
            </FormGroup>
            {object.client_id && (
              <FormGroup label={<span className="fb-label">Location</span>} labelFor="job-location">
                <ControlGroup fill={true} vertical={false} className="fb-picker">
                  <LocationAutocomplete
                    scope={{ client_id: object.client_id }}
                    inputProps={{
                      id: "job-location",
                      fill: true,
                      placeholder: "Search this client's locations"
                    }}
                    onLocationClear={this.onLocationClear.bind(this)}
                    value={object.location}
                    onLocationSelect={location => {
                      if (object && location) {
                        object.location_id = location.id;
                        object.location = location;
                      } else {
                        this.onLocationClear();
                      }
                    }}
                  />
                  <Button
                    icon="add"
                    text="New location"
                    onClick={() => {
                      this.isOpenAddLocationDialog = true;
                    }}
                    className="fb-add-button"
                  />
                </ControlGroup>
              </FormGroup>
            )}
          </FormSection>

          <FormSection title="The work" hint="What needs doing. The title shows on the dispatch board.">
            <div className="jf-grid">
              <div
                id={FIELD_IDS.service_type}
                className={`jf-field${errors.service_type ? ' has-error' : ''}`}
                role="group"
                aria-labelledby="job-service-type-label"
              >
                <span className="fb-label" id="job-service-type-label">
                  Service type<span className="fb-required" aria-hidden="true"> *</span>
                </span>
                <ServiceTypePicker
                  selectedOptions={object.selectedServiceType}
                  onChange={this.onServiceTypeSelect.bind(this)}
                  maximumSelections={1}
                  placeholder="Choose a service type"
                />
                <FieldError>{errors.service_type}</FieldError>
              </div>
              <FormGroup
                label={<span className="fb-label">Title<span className="fb-required" aria-hidden="true"> *</span></span>}
                labelFor="job-title"
                className={errors.title ? 'has-error' : ''}
              >
                <div id={FIELD_IDS.title}>
                  <input
                    id="job-title"
                    type="text"
                    className="bp3-input bp3-fill"
                    placeholder="e.g. No heat – furnace check"
                    aria-required="true"
                    aria-invalid={Boolean(errors.title)}
                    value={object.title || ''}
                    onChange={event => {
                      object.title = event.target.value;
                    }}
                  />
                </div>
                <FieldError>{errors.title}</FieldError>
              </FormGroup>
            </div>
            <FormGroup
              label={<span className="fb-label">Reason for call<span className="fb-required" aria-hidden="true"> *</span></span>}
              labelFor="job-reason"
              className={errors.reason ? 'has-error' : ''}
            >
              <div id={FIELD_IDS.reason}>
                <TextArea
                  id="job-reason"
                  rows={3}
                  className="bp3-fill"
                  placeholder="e.g. Furnace not starting since last night; thermostat shows error E3."
                  aria-required="true"
                  aria-invalid={Boolean(errors.reason)}
                  value={object.reason_for_call || ''}
                  onChange={event => {
                    object.reason_for_call = event.target.value;
                  }}
                />
              </div>
              <FieldError>{errors.reason}</FieldError>
            </FormGroup>
            <Choice
              label="Priority"
              name="Priority"
              value={priority === 'low' ? 'normal' : priority}
              onChange={(value) => { object.priority = value; }}
              options={PRIORITIES}
            />
            <div className="jf-grid jf-grid--wide-first">
              {object.client_id && (
                <FormGroup label={<span className="fb-label">Project</span>} labelFor="job-project">
                  <ControlGroup fill={true} vertical={false} className="fb-picker">
                    <ProjectAutocomplete
                      scope={{ client_id: object.client_id }}
                      selectedProjectId={object.project_id}
                      inputProps={{
                        id: "job-project",
                        fill: true,
                        placeholder: "Search projects"
                      }}
                      onProjectClear={() => {
                        object.project_id = null;
                      }}
                      onProjectSelect={project => {
                        if (object && project) {
                          object.project_id = project.id;
                        } else {
                          object.project_id = null;
                        }
                      }}
                    />
                    <Button
                      icon="add"
                      aria-label="New project"
                      title="New project"
                      onClick={() => {
                        this.isOpenAddProjectDialog = true;
                      }}
                      className="fb-add-button"
                    />
                  </ControlGroup>
                </FormGroup>
              )}
              <FormGroup
                label={<span className="fb-label">PO number <span className="bp3-text-muted">(optional)</span></span>}
                labelFor="job-po-number"
              >
                <input
                  id="job-po-number"
                  type="text"
                  className="bp3-input bp3-fill"
                  placeholder="e.g. PO-10442"
                  defaultValue={object.po_number}
                  onChange={event => {
                    object.po_number = event.target.value;
                  }}
                />
              </FormGroup>
            </div>
          </FormSection>

          <FormSection
            title="Schedule"
            hint="When the team arrives and who's going."
            aside={
              <div className="fb-switch" role="radiogroup" aria-label="Set the time by">
                {SCHEDULE_MODES.map(mode => (
                  <button
                    key={mode.value}
                    type="button"
                    role="radio"
                    aria-checked={this.scheduleMode === mode.value}
                    className={`fb-switch__option${this.scheduleMode === mode.value ? ' is-selected' : ''}`}
                    onClick={() => this.setScheduleMode(mode.value)}
                  >
                    {mode.label}
                  </button>
                ))}
              </div>
            }
          >
            {this.scheduleMode === 'day' ? (
              <WhenPicker
                start={object.start_date}
                end={object.end_date}
                onStartChange={this.onStartDateChange.bind(this)}
                onEndChange={this.onEndDateChange.bind(this)}
                error={errors.end}
                endId={FIELD_IDS.end}
              />
            ) : (
              <div className="jf-grid">
                <div className="jf-field" role="group" aria-labelledby="job-start-label">
                  <span className="fb-label" id="job-start-label">
                    Start<span className="fb-required" aria-hidden="true"> *</span>
                  </span>
                  <DateTimeInput
                    placeholder="Start date"
                    defaultValue={object.start_date}
                    onChange={this.onStartDateChange.bind(this)}
                  />
                </div>
                <div
                  id={FIELD_IDS.end}
                  className={`jf-field${errors.end ? ' has-error' : ''}`}
                  role="group"
                  aria-labelledby="job-end-label"
                >
                  <span className="fb-label" id="job-end-label">
                    End<span className="fb-required" aria-hidden="true"> *</span>
                  </span>
                  <DateTimeInput
                    placeholder="End date"
                    defaultValue={object.end_date}
                    onChange={this.onEndDateChange.bind(this)}
                  />
                  <FieldError>{errors.end}</FieldError>
                </div>
              </div>
            )}
            {this.techsVacationConflicts && this.techsVacationConflicts.length > 0 && <div className="alert alert-danger" role="alert">
              {this.techsVacationConflicts.map((conflict, index) => {
                return (
                  <p key={index}>
                    {conflict.message}
                  </p>
                )
              })}
            </div>}
            <div className="jf-field" role="group" aria-labelledby="job-techs-label">
              <span className="fb-label" id="job-techs-label">Technicians</span>
              <TechnicianPicker
                includeAdmins={true}
                includeSelectAll={true}
                selectedOptions={object.technician_ids || ""}
                onChange={this.onTechnicianChange.bind(this)}
                shouldDismissPopover={false}
                showSelected={true}
              />
            </div>
          </FormSection>

          {object.work_report_ids && object.work_report_ids.length > 0 && (
            <input
              type="hidden"
              name="work_report_ids"
              value={JSON.stringify(object.work_report_ids)}
            />
          )}
        </FormDrawer>
        {/* Outside the panel, so Escape in one of these closes only it. */}
        <AddProjectModal
          isOpen={this.isOpenAddProjectDialog}
          client_id={object.client_id}
          onClose={this.handleCloseProjectDailog.bind(this)}
          onSuccess={this.onSuccessAddProject.bind(this)}
        />
        <AddLocationModal
          isOpen={this.isOpenAddLocationDialog}
          client_id={object.client_id}
          onClose={this.handleCloseLocationDailog.bind(this)}
          onSuccess={this.onSuccessAddLocation.bind(this)}
        />
        <AddClientModal
          isOpen={this.isOpenAddClientDialog}
          onClose={this.handleCloseClientDailog.bind(this)}
          onSuccess={this.onSuccessAddClient.bind(this)}
        />
      </React.Fragment>
    );
  }
}

export default PersistJob;
